import { proStudioUrqlGql, urqlGql } from '@pypestream/api-services';
import { useEffect, useRef } from 'react';
import { sendProStudioEvent } from '../xstate/app.xstate';
import type { SolutionUserPresenceItem } from '../xstate/prostudio.xstate.types';

const PRESENCE_INTERVAL_MS = 15_000;

function mapPresence(
  raw: Array<{
    id: string;
    userId: string;
    firstSeenAt: unknown;
    lastSeenAt: unknown;
    isActive: boolean;
    pagePath: string;
    solutionId: string;
    user?: {
      id: string;
      firstName: string;
      lastName: string;
      picture?: string | null;
    };
  }>
): SolutionUserPresenceItem[] {
  return raw.map((row) => ({
    id: row.id,
    firstSeenAt: row.firstSeenAt,
    lastSeenAt: row.lastSeenAt,
    isActive: row.isActive,
    pagePath: row.pagePath,
    solutionId: row.solutionId,
    userId: row.userId,
    user: row.user,
  }));
}

export function useSolutionPresence(
  isFirstLevelPage: boolean,
  solutionId: string | undefined,
  pagePath: string
) {
  const pathRef = useRef(pagePath);

  useEffect(() => {
    pathRef.current = pagePath;
    if (isFirstLevelPage) {
      sendProStudioEvent({ type: 'stopPresence' });
      return;
    }
    if (!solutionId || !pagePath) return;

    sendProStudioEvent({
      type: 'startPresence',
      solutionId,
      pagePath,
    });
    sendProStudioEvent({ type: 'presenceRouteChange', pagePath });

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let subscriptionHandle: { unsubscribe?: () => void } | (() => void) | null =
      null;

    const isPageActive = () =>
      typeof document !== 'undefined' &&
      !document.hidden &&
      document.hasFocus();

    const runMutation = () => {
      if (!isPageActive()) return;
      void proStudioUrqlGql
        .upsertSolutionUserPresence({
          solutionId,
          pagePath: pathRef.current,
        })
        .toPromise()
        .then(() => scheduleNext())
        .catch(() => scheduleNext());
    };

    const scheduleNext = () => {
      if (!isPageActive()) return;
      if (timeoutId != null) return;
      timeoutId = setTimeout(() => {
        timeoutId = null;
        runMutation();
      }, PRESENCE_INTERVAL_MS);
    };

    const startInterval = () => {
      if (timeoutId != null) return;
      if (!isPageActive()) return;
      runMutation();
    };

    const stopInterval = () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    try {
      const sub = urqlGql.solutionUserPresenceSubscription({ solutionId });
      subscriptionHandle = sub.subscribe((result: unknown) => {
        const res = result as {
          error?: unknown;
          data?: { tenancy_solution_user_presence?: unknown[] };
        };
        if (res.error) return;
        const list = res.data?.tenancy_solution_user_presence;
        if (list && Array.isArray(list)) {
          sendProStudioEvent({
            type: 'setPresenceData',
            presences: mapPresence(list as Parameters<typeof mapPresence>[0]),
          });
        }
      });
    } catch (_err) {
      // subscription setup failed
    }

    startInterval();

    const onVisibilityChange = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        stopInterval();
      } else {
        startInterval();
      }
    };

    const onWindowBlur = () => stopInterval();
    const onWindowFocus = () => startInterval();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
      stopInterval();
      const handle = subscriptionHandle as
        | (() => void)
        | { unsubscribe?: () => void }
        | null;
      if (handle) {
        if (typeof handle === 'function') {
          handle();
        } else if (typeof handle?.unsubscribe === 'function') {
          handle.unsubscribe();
        }
      }
      sendProStudioEvent({ type: 'stopPresence' });
    };
  }, [isFirstLevelPage, solutionId, pagePath]);
}
